import { Link } from 'react-router-dom';
import { Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { IconPlus } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { useChecklistGroups, useChecklists } from './api';
import { checklistsDict } from './i18n';
import { groupedLists, progress } from './lists';

/**
 * A section per group, in the user's order, each list with its kind and how
 * much of it is ticked. An empty group still shows, with a note.
 */
export function ChecklistOverview() {
  const t = useDict(checklistsDict);
  usePageTitle(t.titleSuffix);
  const { data: lists } = useChecklists();
  const { data: groups } = useChecklistGroups();

  const header = (
    <PageHeader
      title={t.checklists}
      actions={
        <Link
          to="/checklists/new"
          className="btn-primary-icon"
          aria-label={t.newChecklist}
          title={t.newChecklist}
        >
          <IconPlus />
        </Link>
      }
    />
  );

  if (!lists || !groups) return header;

  // No groups means no lists: a group holding one can't be deleted.
  if (groups.length === 0)
    return (
      <>
        {header}
        <EmptyState size="lg">
          {t.empty}{' '}
          <Link to="/checklists/new" className="text-link">
            {t.addFirst}
          </Link>
        </EmptyState>
      </>
    );

  return (
    <>
      {header}
      {groupedLists(groups, lists).map(({ group, lists: inGroup }) => (
        <Panel key={group.id} className="checklist-overview-card">
          <h2 className="heading-section checklist-group-heading">
            {group.name}
          </h2>
          {inGroup.length === 0 ? (
            <p className="text-hint checklist-group-empty">{t.groupEmpty}</p>
          ) : (
            <ul className="checklist-overview">
              {inGroup.map((list) => {
                const { done, total } = progress(list, lists);
                return (
                  <li key={list.id}>
                    <Link
                      to={`/checklists/${list.id}`}
                      className="checklist-row"
                    >
                      <span className="checklist-row-name">{list.name}</span>
                      <Badge tone="neutral">
                        {list.kind === 'combined' ? t.combined : t.standard}
                      </Badge>
                      <span
                        className="checklist-row-progress"
                        aria-label={t.progressLabel(done, total)}
                      >
                        {t.progress(done, total)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      ))}
    </>
  );
}
